/**
 * ProjectAnalysisInline - Volet "Analyse" integre directement dans la fiche projet.
 *
 * Lot 1 (Fev 2026) : evite a Julie/Carla de jongler entre l'onglet Analyses et la fiche
 * Projet. Foncier (prix, frais d'acquisition), Loyer (actuel + projete + lots),
 * Rentabilite (KPIs calcules en live), tout est editable sans changer d'onglet.
 *
 * Auto-save debounce 1.2s via PUT /api/analyses/{id} (meme endpoint que l'editeur complet).
 * Si le projet n'a pas encore d'analyse_id, on affiche un CTA pour en creer une.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { computeAnalysisLive, unitMonthlyRent } from '../AnalysesPage';
import { AutoSaveIndicator } from '../hooks/useAutoSave';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n == null || isNaN(n)) return '—';
  return Math.round(n).toLocaleString('fr-FR') + ' EUR';
};

const VOLATILE_KEYS = ['computed', 'created_at', 'updated_at', 'created_by', 'analysis_id'];
const stripVolatile = (obj) => {
  if (!obj) return null;
  const next = { ...obj };
  VOLATILE_KEYS.forEach((k) => delete next[k]);
  return next;
};

const ProjectAnalysisInline = ({ project, onAnalysisCreated }) => {
  const analysisId = project?.analysis_id || null;
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [autoSaveError, setAutoSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [creating, setCreating] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Refs pour le moteur d'auto-save (debounce + anti-collision)
  const lastSavedJsonRef = useRef(null);
  const saveTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const formRef = useRef(null);

  const fetchAnalysis = useCallback(async () => {
    if (!analysisId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/analyses/${analysisId}`);
      const initial = {
        ...r.data,
        financing: r.data.financing || { down_payment: 0, loan_duration_years: 25, interest_rate: 3.65, insurance_rate: 0.36 },
      };
      setForm(initial);
      lastSavedJsonRef.current = JSON.stringify(stripVolatile(initial));
      setAutoSaveStatus('saved');
      setSavedAt(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [analysisId]);

  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);
  useEffect(() => { formRef.current = form; }, [form]);

  // Auto-save 1.2s
  useEffect(() => {
    if (!form || !analysisId) return;
    const currentJson = JSON.stringify(stripVolatile(form));
    if (lastSavedJsonRef.current === null) {
      lastSavedJsonRef.current = currentJson;
      return;
    }
    if (currentJson === lastSavedJsonRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setAutoSaveStatus('saving');
    saveTimerRef.current = setTimeout(async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const snapshot = formRef.current;
        const payload = stripVolatile(snapshot);
        await axios.put(`${API}/analyses/${analysisId}`, payload);
        lastSavedJsonRef.current = JSON.stringify(stripVolatile(snapshot));
        setAutoSaveStatus('saved');
        setSavedAt(new Date());
        setAutoSaveError(null);
      } catch (e) {
        setAutoSaveStatus('error');
        setAutoSaveError(e.response?.data?.detail || e.message);
      } finally {
        inFlightRef.current = false;
      }
    }, 1200);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [form, analysisId]);

  const live = useMemo(() => form ? computeAnalysisLive(form) : null, [form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const createAnalysisFromProject = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/analyses`, {
        name: project.name || 'Analyse projet',
        client_name: project.client_name || '',
        city: project.city || '',
        property_type: 'ancien',
        project_id: project.project_id,
      });
      const newId = r.data.analysis_id;
      // Le backend doit lier project.analysis_id automatiquement. On notifie quand meme.
      if (onAnalysisCreated) onAnalysisCreated(newId);
      // Reload via parent ; rien d'autre a faire.
    } catch (e) {
      alert('Erreur creation analyse : ' + (e.response?.data?.detail || e.message));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="realease-card p-6" data-testid="project-analysis-inline-loading">
        <p className="text-sm text-gray-400 italic">Chargement de l&apos;analyse...</p>
      </div>
    );
  }

  if (!analysisId) {
    return (
      <div className="realease-card p-6" data-testid="project-analysis-inline-empty">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#008A8D]/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-lg">Analyse de rentabilite</h3>
            <p className="text-sm text-gray-500 mt-1">
              Aucune analyse n&apos;est encore liee a ce projet. Creez-en une pour visualiser ici le foncier,
              les loyers et la rentabilite, editables sans changer d&apos;onglet.
            </p>
            <button
              type="button"
              onClick={createAnalysisFromProject}
              disabled={creating}
              className="mt-3 px-4 py-2 bg-[#008A8D] text-white text-sm font-semibold rounded-lg hover:bg-[#006B6D] transition disabled:opacity-50"
              data-testid="project-analysis-create-btn"
            >
              {creating ? 'Creation...' : '+ Creer une analyse pour ce projet'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="realease-card p-6" data-testid="project-analysis-inline-error">
        <p className="text-sm text-red-600">Erreur chargement analyse : {error}</p>
      </div>
    );
  }

  if (!form || !live) return null;

  const cashflowOk = (live.monthlyCashflow || 0) >= 0;

  return (
    <div className="realease-card p-0 overflow-hidden" data-testid="project-analysis-inline">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#008A8D]/10 to-[#13736D]/5 border-b border-[#008A8D]/20 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="font-semibold text-gray-900">Analyse de rentabilite</h3>
          <span className="text-[10px] uppercase tracking-wide text-gray-500 ml-1">Auto-save</span>
        </div>
        <div className="flex items-center gap-3">
          <AutoSaveIndicator status={autoSaveStatus} savedAt={savedAt} error={autoSaveError} />
          <a
            href={`/analyses/${analysisId}`}
            className="text-xs text-[#008A8D] hover:underline font-medium"
            data-testid="project-analysis-open-full"
          >
            Vue complete &rarr;
          </a>
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="text-xs text-gray-500 hover:text-gray-700"
            data-testid="project-analysis-toggle"
          >
            {collapsed ? 'Deplier' : 'Replier'}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-5 space-y-5">
          {/* KPIs rentabilite live */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="project-analysis-kpis">
            <div className="p-3 rounded-lg bg-[#008A8D]/5 border border-[#008A8D]/30">
              <p className="text-[10px] uppercase text-[#008A8D] font-semibold tracking-wide">Rendement brut</p>
              <p className="text-xl font-bold text-[#008A8D] mt-1">{(live.grossYield || 0).toFixed(2)} %</p>
            </div>
            <div className="p-3 rounded-lg bg-[#008A8D]/5 border border-[#008A8D]/30">
              <p className="text-[10px] uppercase text-[#008A8D] font-semibold tracking-wide">Rendement net</p>
              <p className="text-xl font-bold text-[#008A8D] mt-1">{(live.netYield || 0).toFixed(2)} %</p>
            </div>
            <div className={`p-3 rounded-lg border ${cashflowOk ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <p className={`text-[10px] uppercase font-semibold tracking-wide ${cashflowOk ? 'text-emerald-700' : 'text-red-700'}`}>Cashflow / mois</p>
              <p className={`text-xl font-bold mt-1 ${cashflowOk ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(live.monthlyCashflow)}</p>
            </div>
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-[10px] uppercase text-gray-600 font-semibold tracking-wide">Mensualite</p>
              <p className="text-xl font-bold text-gray-800 mt-1">{fmt(live.effectiveMonthly)}</p>
              <p className="text-[10px] text-gray-500">
                {live.monthlyOverrideActive ? 'credit obtenu (saisie)' : 'estimee : capital + assurance'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-[10px] uppercase text-amber-700 font-semibold tracking-wide">Operation totale</p>
              <p className="text-xl font-bold text-amber-800 mt-1">{fmt(live.totalOperation)}</p>
            </div>
          </div>

          {/* Foncier + Frais */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="p-4 rounded-lg border border-gray-200" data-testid="project-analysis-foncier">
              <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-[#008A8D] rounded"></span>
                Foncier &amp; frais d&apos;acquisition
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Prix affiche</label>
                  <input type="number" value={form.asking_price || ''}
                         onChange={(e) => set('asking_price', +e.target.value || 0)}
                         className="realease-input mt-1 text-sm"
                         data-testid="project-analysis-asking-price" />
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Prix cible / negocie</label>
                  <input type="number" value={form.target_price || ''}
                         onChange={(e) => set('target_price', +e.target.value || 0)}
                         className="realease-input mt-1 text-sm"
                         data-testid="project-analysis-target-price" />
                  <p className="text-[10px] text-gray-400 mt-0.5">Negociation : {(live.negotiationPct || 0).toFixed(2)} %</p>
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Type de bien</label>
                  <select value={form.property_type || 'ancien'}
                          onChange={(e) => set('property_type', e.target.value)}
                          className="realease-input mt-1 text-sm"
                          data-testid="project-analysis-property-type">
                    <option value="ancien">Ancien (8,5 %)</option>
                    <option value="neuf">Neuf (2,5 %)</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Surface (m²)</label>
                  <input type="number" value={form.surface || ''}
                         onChange={(e) => set('surface', +e.target.value || 0)}
                         className="realease-input mt-1 text-sm"
                         data-testid="project-analysis-surface" />
                  <p className="text-[10px] text-gray-400 mt-0.5">{live.pricePerSqm ? Math.round(live.pricePerSqm).toLocaleString('fr-FR') + ' EUR/m²' : '—'}</p>
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Frais notaire</label>
                  <input type="number" value={form.notaire_fees ?? ''}
                         onChange={(e) => set('notaire_fees', e.target.value === '' ? null : +e.target.value)}
                         placeholder={fmt(live.notaireFeesAuto).replace(' EUR', '')}
                         className="realease-input mt-1 text-sm"
                         data-testid="project-analysis-notaire-fees" />
                  <p className="text-[10px] text-gray-400 mt-0.5">Auto : {fmt(live.notaireFeesAuto)}</p>
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Frais agence</label>
                  <input type="number" value={form.agency_fees || ''}
                         onChange={(e) => set('agency_fees', +e.target.value || 0)}
                         className="realease-input mt-1 text-sm"
                         data-testid="project-analysis-agency-fees" />
                </div>
                {/* Iter 96 : ligne read-only Honoraires RealEase (calcul auto) */}
                <div className="col-span-2 p-2 rounded-md bg-[#13736D]/5 border border-[#13736D]/20 flex items-center justify-between mt-1">
                  <div>
                    <p className="text-[11px] uppercase font-semibold text-[#13736D] tracking-wide">Honoraires RealEase</p>
                    <p className="text-[10px] text-gray-500">Base : {fmt(live.realeaseBase)} {live.realeaseFee?.pct ? `· ${live.realeaseFee.pct}%` : ''}</p>
                  </div>
                  <p className="text-base font-bold text-[#13736D]" data-testid="project-analysis-realease-fee">{fmt(live.realeaseFee?.amount)}</p>
                </div>
              </div>
            </div>

            {/* Iter 97 : Travaux & mobilier - exposes dans la vue inline (etaient deja dans le modele) */}
            <div className="p-4 rounded-lg border border-gray-200" data-testid="project-analysis-works">
              <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-orange-500 rounded"></span>
                Travaux &amp; mobilier
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Budget travaux</label>
                  <input type="number"
                         value={live.worksItemsTotal > 0 ? live.worksItemsTotal : (form.works_budget || '')}
                         disabled={live.worksItemsTotal > 0}
                         onChange={(e) => set('works_budget', +e.target.value || 0)}
                         className={`realease-input mt-1 text-sm ${live.worksItemsTotal > 0 ? 'bg-gray-100' : ''}`}
                         data-testid="project-analysis-works-budget" />
                  {live.worksItemsTotal > 0 && (
                    <p className="text-[10px] text-gray-400 mt-0.5">Calcule depuis les lignes detaillees ({live.worksItemsTotal.toLocaleString('fr-FR')} EUR)</p>
                  )}
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Mobilier</label>
                  <input type="number"
                         value={form.furniture_budget || ''}
                         onChange={(e) => set('furniture_budget', +e.target.value || 0)}
                         className="realease-input mt-1 text-sm"
                         data-testid="project-analysis-furniture-budget" />
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Raccordement ENEDIS</label>
                  <input type="number"
                         value={form.enedis_fees || ''}
                         onChange={(e) => set('enedis_fees', +e.target.value || 0)}
                         className="realease-input mt-1 text-sm"
                         data-testid="project-analysis-enedis-fees" />
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Raccordement eau</label>
                  <input type="number"
                         value={form.water_fees || ''}
                         onChange={(e) => set('water_fees', +e.target.value || 0)}
                         className="realease-input mt-1 text-sm"
                         data-testid="project-analysis-water-fees" />
                </div>
                <div className="col-span-2 p-2 rounded-md bg-orange-50 border border-orange-200 flex items-center justify-between mt-1">
                  <div>
                    <p className="text-[11px] uppercase font-semibold text-orange-800 tracking-wide">Enveloppe travaux + mobilier</p>
                    <p className="text-[10px] text-orange-700">Travaux + mobilier + raccordements (impacte la base honoraires)</p>
                  </div>
                  <p className="text-base font-bold text-orange-900" data-testid="project-analysis-works-envelope">
                    {fmt(live.worksBudget + live.furniture + live.enedis)}
                  </p>
                </div>
              </div>
            </div>

            {/* Loyer */}
            <div className="p-4 rounded-lg border border-gray-200" data-testid="project-analysis-loyer">
              <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-1.5 h-5 bg-[#A57F4F] rounded"></span>
                Revenus locatifs
              </h4>
              {/* Lots */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                    Lots / appartements
                  </p>
                  <button
                    type="button"
                    onClick={() => set('rental_units', [...(form.rental_units || []), { label: `Lot ${(form.rental_units || []).length + 1}`, monthly_rent: 0, current_monthly_rent: 0 }])}
                    className="text-xs text-[#A57F4F] font-semibold hover:underline"
                    data-testid="project-analysis-add-rental-unit"
                  >
                    + Ajouter un lot
                  </button>
                </div>
                {(form.rental_units || []).length === 0 ? (
                  <p className="text-[11px] text-gray-400 italic mb-1">Aucun lot detaille. Vous pouvez saisir le loyer total ci-dessous.</p>
                ) : (
                  <div className="space-y-1.5">
                    {(form.rental_units || []).map((u, i) => {
                      const stRent = unitMonthlyRent(u);
                      return (
                      <div key={i} data-testid={`project-analysis-rental-unit-${i}`}>
                        <div className="grid grid-cols-12 gap-1.5 items-center">
                        <input type="text" value={u.label || ''}
                               onChange={(e) => { const arr = [...(form.rental_units || [])]; arr[i] = { ...arr[i], label: e.target.value }; set('rental_units', arr); }}
                               placeholder="Lot..." className="col-span-5 realease-input text-xs" />
                        <input type="number" value={u.current_monthly_rent ?? ''}
                               onChange={(e) => { const arr = [...(form.rental_units || [])]; arr[i] = { ...arr[i], current_monthly_rent: +e.target.value || 0 }; set('rental_units', arr); }}
                               placeholder="Actuel" className="col-span-3 realease-input text-xs text-right" />
                        <input type="number" value={u.short_term ? stRent : (u.monthly_rent ?? '')}
                               disabled={!!u.short_term}
                               onChange={(e) => { const arr = [...(form.rental_units || [])]; arr[i] = { ...arr[i], monthly_rent: +e.target.value || 0 }; set('rental_units', arr); }}
                               placeholder="Cible" className={`col-span-3 realease-input text-xs text-right ${u.short_term ? 'bg-gray-100' : ''}`} />
                        <button
                          type="button"
                          onClick={() => set('rental_units', (form.rental_units || []).filter((_, j) => j !== i))}
                          className="col-span-1 text-red-500 hover:bg-red-50 rounded"
                          data-testid={`project-analysis-remove-rental-unit-${i}`}
                        >×</button>
                        </div>
                        <div className="pl-0.5 mt-0.5 mb-1.5">
                          <label className="inline-flex items-center gap-1 text-[10px] text-gray-600 cursor-pointer">
                            <input type="checkbox" checked={!!u.short_term}
                                   onChange={(e) => { const arr = [...(form.rental_units || [])]; arr[i] = { ...arr[i], short_term: e.target.checked, monthly_rent: e.target.checked ? unitMonthlyRent({ ...arr[i], short_term: true }) : (arr[i].monthly_rent || 0) }; set('rental_units', arr); }}
                                   className="accent-[#008A8D]"
                                   data-testid={`project-analysis-rental-unit-shortterm-${i}`} />
                            Courte durée (nuitée × remplissage)
                          </label>
                          {u.short_term && (() => {
                            const upd = (patch) => { const arr = [...(form.rental_units || [])]; const next = { ...arr[i], ...patch }; next.monthly_rent = unitMonthlyRent(next); arr[i] = next; set('rental_units', arr); };
                            return (
                            <div className="mt-1 bg-[#008A8D]/5 border border-[#008A8D]/15 rounded p-2">
                              <div className="grid grid-cols-12 gap-1.5 items-end">
                              <div className="col-span-4">
                                <label className="text-[9px] font-semibold text-gray-500 uppercase">{u.seasonal ? 'Nuitée BS (EUR)' : 'Nuitée (EUR)'}</label>
                                <input type="number" value={u.nightly_rate ?? ''}
                                       onChange={(e) => upd({ nightly_rate: +e.target.value || 0 })}
                                       placeholder="80" className="realease-input text-xs text-right"
                                       data-testid={`project-analysis-rental-unit-nightly-${i}`} />
                              </div>
                              <div className="col-span-4">
                                <label className="text-[9px] font-semibold text-gray-500 uppercase">{u.seasonal ? 'Rempl. BS (%)' : 'Remplissage (%)'}</label>
                                <input type="number" min="0" max="100" value={u.occupancy_rate ?? ''}
                                       onChange={(e) => upd({ occupancy_rate: Math.min(100, Math.max(0, +e.target.value || 0)) })}
                                       placeholder="65" className="realease-input text-xs text-right"
                                       data-testid={`project-analysis-rental-unit-occupancy-${i}`} />
                              </div>
                              <div className="col-span-4 text-right pb-0.5">
                                <p className="text-[11px] text-gray-700">≈ <b className="text-[#008A8D]">{fmt(stRent)}</b> / mois</p>
                              </div>
                              </div>
                              {u.seasonal && (
                                <div className="grid grid-cols-12 gap-1.5 items-end mt-1.5 pt-1.5 border-t border-[#008A8D]/15">
                                  <div className="col-span-4">
                                    <label className="text-[9px] font-semibold text-gray-500 uppercase">Nuitée HS (EUR)</label>
                                    <input type="number" value={u.hs_nightly_rate ?? ''}
                                           onChange={(e) => upd({ hs_nightly_rate: +e.target.value || 0 })}
                                           placeholder="120" className="realease-input text-xs text-right"
                                           data-testid={`project-analysis-rental-unit-hs-nightly-${i}`} />
                                  </div>
                                  <div className="col-span-4">
                                    <label className="text-[9px] font-semibold text-gray-500 uppercase">Rempl. HS (%)</label>
                                    <input type="number" min="0" max="100" value={u.hs_occupancy_rate ?? ''}
                                           onChange={(e) => upd({ hs_occupancy_rate: Math.min(100, Math.max(0, +e.target.value || 0)) })}
                                           placeholder="85" className="realease-input text-xs text-right"
                                           data-testid={`project-analysis-rental-unit-hs-occupancy-${i}`} />
                                  </div>
                                  <div className="col-span-4">
                                    <label className="text-[9px] font-semibold text-gray-500 uppercase">Mois en HS</label>
                                    <input type="number" min="0" max="12" value={u.hs_months ?? ''}
                                           onChange={(e) => upd({ hs_months: Math.min(12, Math.max(0, +e.target.value || 0)) })}
                                           placeholder="4" className="realease-input text-xs text-right"
                                           data-testid={`project-analysis-rental-unit-hs-months-${i}`} />
                                  </div>
                                </div>
                              )}
                              <label className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-gray-600 cursor-pointer">
                                <input type="checkbox" checked={!!u.seasonal}
                                       onChange={(e) => upd(e.target.checked
                                         ? { seasonal: true, hs_months: u.hs_months || 4, hs_nightly_rate: u.hs_nightly_rate || u.nightly_rate || 0, hs_occupancy_rate: u.hs_occupancy_rate || u.occupancy_rate || 0 }
                                         : { seasonal: false })}
                                       className="accent-[#008A8D]"
                                       data-testid={`project-analysis-rental-unit-seasonal-${i}`} />
                                Saisonnalité (haute / basse saison)
                              </label>
                              <div className="mt-1.5 pt-1.5 border-t border-[#008A8D]/15 grid grid-cols-12 gap-1.5 items-center">
                                <label className="col-span-7 text-[9px] font-semibold text-gray-500 uppercase">Loyer équiv. location classique</label>
                                <input type="number" value={u.lt_monthly_rent ?? ''}
                                       onChange={(e) => upd({ lt_monthly_rent: +e.target.value || 0 })}
                                       placeholder="650" className="col-span-5 realease-input text-xs text-right"
                                       data-testid={`project-analysis-rental-unit-lt-rent-${i}`} />
                              </div>
                            </div>
                            );
                          })()}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Loyer actuel / mois</label>
                  <input
                    type="number"
                    value={live.unitsCurrentRentTotal > 0 ? live.unitsCurrentRentTotal : (form.current_monthly_rent || '')}
                    disabled={live.unitsCurrentRentTotal > 0}
                    onChange={(e) => set('current_monthly_rent', +e.target.value || 0)}
                    className={`realease-input mt-1 text-sm ${live.unitsCurrentRentTotal > 0 ? 'bg-gray-100' : ''}`}
                    data-testid="project-analysis-current-rent"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Loyer projete / mois</label>
                  <input
                    type="number"
                    value={live.unitsRentTotal > 0 ? live.unitsRentTotal : (form.monthly_rent || '')}
                    disabled={live.unitsRentTotal > 0}
                    onChange={(e) => set('monthly_rent', +e.target.value || 0)}
                    className={`realease-input mt-1 text-sm ${live.unitsRentTotal > 0 ? 'bg-gray-100' : ''}`}
                    data-testid="project-analysis-projected-rent"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Autres revenus / mois</label>
                  <input type="number" value={form.other_income || ''}
                         onChange={(e) => set('other_income', +e.target.value || 0)}
                         className="realease-input mt-1 text-sm"
                         data-testid="project-analysis-other-income" />
                </div>
                <div>
                  <label className="text-[11px] uppercase font-semibold text-gray-600">Total annuel projete</label>
                  <p className="realease-input mt-1 text-sm bg-gray-50 text-gray-600 font-semibold">{fmt(live.annualRent)}</p>
                </div>
              </div>
              {live.hasCurrentRent && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]" data-testid="project-analysis-current-vs-projected">
                  <div className="p-2 rounded border border-gray-200 bg-white">
                    <p className="text-gray-500 uppercase text-[10px]">Actuel</p>
                    <p className="font-bold text-gray-800">{live.currentNetYield.toFixed(2)} % net</p>
                    <p className={`text-xs ${live.currentMonthlyCashflow >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {fmt(live.currentMonthlyCashflow)} / mois
                    </p>
                  </div>
                  <div className="p-2 rounded border border-[#008A8D]/30 bg-[#008A8D]/5">
                    <p className="text-[#008A8D] uppercase text-[10px] font-bold">Apres optimisation</p>
                    <p className="font-bold text-[#008A8D]">{live.netYield.toFixed(2)} % net</p>
                    <p className={`text-xs ${live.monthlyCashflow >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {fmt(live.monthlyCashflow)} / mois
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Iter 96 : Financement obtenu - permet d'overrider la mensualite avec le credit reel */}
          <div className="p-4 rounded-lg border border-gray-200 bg-gradient-to-r from-blue-50/50 to-transparent" data-testid="project-analysis-financing">
            <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-1.5 h-5 bg-blue-600 rounded"></span>
              Financement obtenu
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-[11px] uppercase font-semibold text-gray-600">Apport</label>
                <input type="number"
                       value={form.financing?.down_payment || ''}
                       onChange={(e) => set('financing', { ...(form.financing || {}), down_payment: +e.target.value || 0 })}
                       className="realease-input mt-1 text-sm"
                       data-testid="project-analysis-down-payment" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-gray-600">Duree (annees)</label>
                <input type="number"
                       value={form.financing?.loan_duration_years || ''}
                       onChange={(e) => set('financing', { ...(form.financing || {}), loan_duration_years: +e.target.value || 0 })}
                       className="realease-input mt-1 text-sm"
                       data-testid="project-analysis-duration" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-gray-600">Taux (%)</label>
                <input type="number" step="0.01"
                       value={form.financing?.interest_rate || ''}
                       onChange={(e) => set('financing', { ...(form.financing || {}), interest_rate: +e.target.value || 0 })}
                       className="realease-input mt-1 text-sm"
                       data-testid="project-analysis-rate" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-gray-600">Assurance (%)</label>
                <input type="number" step="0.01"
                       value={form.financing?.insurance_rate || ''}
                       onChange={(e) => set('financing', { ...(form.financing || {}), insurance_rate: +e.target.value || 0 })}
                       className="realease-input mt-1 text-sm"
                       data-testid="project-analysis-insurance-rate" />
              </div>
            </div>
            <div className="mt-3 p-3 rounded-md bg-blue-50 border border-blue-200 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2">
                <label className="text-[11px] uppercase font-semibold text-blue-800 flex items-center gap-1.5">
                  Mensualite reelle (credit obtenu)
                  <span className="text-[9px] font-normal text-blue-600 normal-case italic">facultatif - remplace le calcul</span>
                </label>
                <input
                  type="number"
                  value={form.monthly_payment_override ?? ''}
                  onChange={(e) => set('monthly_payment_override', e.target.value === '' ? null : +e.target.value)}
                  placeholder={`Calcule : ${fmt(live.monthly?.total).replace(' EUR', '')}`}
                  className="realease-input mt-1 text-sm bg-white"
                  data-testid="project-analysis-monthly-override"
                />
                <p className="text-[10px] text-blue-700 mt-1">
                  Renseignez ici la mensualite reellement obtenue par la banque (capital + assurance).
                  Si laisse vide, on utilise l&apos;estimation a partir du taux/duree ci-dessus.
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase text-blue-700 font-semibold">Mensualite utilisee</p>
                <p className="text-2xl font-bold text-blue-800">{fmt(live.effectiveMonthly)}</p>
                <p className="text-[10px] text-blue-600">
                  {live.monthlyOverrideActive ? '(saisie banque)' : `(estimee · ${fmt(live.monthly?.total)})`}
                </p>
              </div>
            </div>

            {/* Iter 104 : Scenarios bancaires - Cashflow mensuel selon duree (20 ans / 25 ans) */}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="project-analysis-scenarios">
              {[
                { years: 20, m: live.monthly20, monCF: live.monthlyCashflow20, annCF: live.annualCashflow20, color: 'amber' },
                { years: 25, m: live.monthly25, monCF: live.monthlyCashflow25, annCF: live.annualCashflow25, color: 'emerald' },
              ].map((s) => {
                const cfOk = (s.monCF || 0) >= 0;
                return (
                  <div
                    key={s.years}
                    className={`p-3 rounded-md border ${cfOk ? 'border-emerald-200 bg-emerald-50/30' : 'border-rose-200 bg-rose-50/30'}`}
                    data-testid={`project-analysis-scenario-${s.years}`}
                  >
                    <div className="flex items-baseline justify-between mb-1">
                      <p className={`text-[11px] uppercase font-semibold ${cfOk ? 'text-emerald-800' : 'text-rose-800'}`}>
                        Scenario {s.years} ans
                      </p>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${cfOk ? 'bg-emerald-200 text-emerald-900' : 'bg-rose-200 text-rose-900'}`}>
                        {cfOk ? 'Cashflow positif' : 'Cashflow negatif'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase">Mensualite</p>
                        <p className="font-bold text-gray-900">{fmt(s.m?.total)}</p>
                        <p className="text-[9px] text-gray-400">capital + assurance</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase">Cashflow / mois</p>
                        <p className={`font-bold text-lg ${cfOk ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(s.monCF)}</p>
                        <p className="text-[9px] text-gray-400">soit {fmt(s.annCF)}/an</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Charges annuelles - iter 96 : liste editable + gestion locative + RealEase */}
          <div className="p-4 rounded-lg border border-gray-200" data-testid="project-analysis-charges">
            <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-1.5 h-5 bg-gray-500 rounded"></span>
              Charges annuelles
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="text-[11px] uppercase font-semibold text-gray-600">Taxe fonciere</label>
                <input type="number" value={form.annual_taxes || ''}
                       onChange={(e) => set('annual_taxes', +e.target.value || 0)}
                       className="realease-input mt-1 text-sm"
                       data-testid="project-analysis-taxes" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-gray-600">Assurance PNO</label>
                <input type="number" value={form.annual_insurance || ''}
                       onChange={(e) => set('annual_insurance', +e.target.value || 0)}
                       className="realease-input mt-1 text-sm"
                       data-testid="project-analysis-insurance" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-gray-600">Comptabilite</label>
                <input type="number" value={form.annual_accounting || ''}
                       onChange={(e) => set('annual_accounting', +e.target.value || 0)}
                       className="realease-input mt-1 text-sm"
                       data-testid="project-analysis-accounting" />
              </div>
              <div>
                <p className="text-[11px] uppercase font-semibold text-gray-600">Total charges</p>
                <p className="realease-input mt-1 text-sm bg-gray-50 text-gray-700 font-semibold" data-testid="project-analysis-total-charges">{fmt(live.annualCharges)}</p>
              </div>
            </div>

            {/* Gestion locative 7% */}
            <div className="p-3 rounded-md bg-amber-50/40 border border-amber-200 mb-3 flex flex-wrap items-center gap-3" data-testid="project-analysis-pm-block">
              <label className="flex items-center gap-2 text-sm font-semibold text-amber-900 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.enable_property_management}
                  onChange={(e) => set('enable_property_management', e.target.checked)}
                  data-testid="project-analysis-pm-toggle"
                />
                Gestion locative
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number" step="0.1"
                  value={form.property_management_rate ?? 7}
                  onChange={(e) => set('property_management_rate', +e.target.value || 0)}
                  disabled={!form.enable_property_management}
                  className="w-16 border border-amber-300 rounded px-1.5 py-0.5 text-sm bg-white text-right disabled:opacity-50"
                  data-testid="project-analysis-pm-rate"
                />
                <span className="text-sm text-amber-900">% du loyer annuel</span>
              </div>
              <div className="ml-auto text-right">
                <p className="text-[10px] uppercase text-amber-700 font-semibold">Cout annuel</p>
                <p className="text-sm font-bold text-amber-900" data-testid="project-analysis-pm-amount">{fmt(live.propertyMgmtAnnual)}</p>
              </div>
            </div>

            {/* Charges custom (liste editable label + montant) */}
            <div data-testid="project-analysis-custom-charges">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Autres charges (detaillees)</p>
                <button
                  type="button"
                  onClick={() => set('custom_annual_charges', [...(form.custom_annual_charges || []), { label: '', amount: 0 }])}
                  className="text-xs text-[#13736D] font-semibold hover:underline"
                  data-testid="project-analysis-add-custom-charge"
                >
                  + Ajouter une charge
                </button>
              </div>
              {(form.custom_annual_charges || []).length === 0 ? (
                <p className="text-[11px] text-gray-400 italic mb-1">
                  Aucune charge detaillee. Cliquez sur &laquo;Ajouter une charge&raquo; pour saisir des charges complementaires (copropriete, syndic, abonnements, etc.).
                </p>
              ) : (
                <div className="space-y-1.5">
                  <div className="grid grid-cols-12 gap-1.5 text-[9px] uppercase text-gray-400 font-semibold">
                    <span className="col-span-5">Nom</span>
                    <span className="col-span-3 text-right">EUR / mois</span>
                    <span className="col-span-3 text-right">EUR / an</span>
                    <span className="col-span-1"></span>
                  </div>
                  {(form.custom_annual_charges || []).map((c, i) => (
                    <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`project-analysis-custom-charge-${i}`}>
                      <input
                        type="text"
                        value={c.label || ''}
                        onChange={(e) => { const arr = [...(form.custom_annual_charges || [])]; arr[i] = { ...arr[i], label: e.target.value }; set('custom_annual_charges', arr); }}
                        placeholder="Ex : Copropriete, syndic, abonnement chaudiere..."
                        className="col-span-5 realease-input text-xs"
                        data-testid={`project-analysis-custom-charge-label-${i}`}
                      />
                      <input
                        type="number"
                        value={c.amount ? Math.round(c.amount / 12 * 100) / 100 : ''}
                        onChange={(e) => { const m = +e.target.value || 0; const arr = [...(form.custom_annual_charges || [])]; arr[i] = { ...arr[i], amount: Math.round(m * 12 * 100) / 100 }; set('custom_annual_charges', arr); }}
                        placeholder="/ mois"
                        className="col-span-3 realease-input text-xs text-right"
                        data-testid={`project-analysis-custom-charge-monthly-${i}`}
                      />
                      <input
                        type="number"
                        value={c.amount ?? ''}
                        onChange={(e) => { const arr = [...(form.custom_annual_charges || [])]; arr[i] = { ...arr[i], amount: +e.target.value || 0 }; set('custom_annual_charges', arr); }}
                        placeholder="/ an"
                        className="col-span-3 realease-input text-xs text-right"
                        data-testid={`project-analysis-custom-charge-amount-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => set('custom_annual_charges', (form.custom_annual_charges || []).filter((_, j) => j !== i))}
                        className="col-span-1 text-red-500 hover:bg-red-50 rounded"
                        title="Supprimer cette charge"
                        data-testid={`project-analysis-custom-charge-remove-${i}`}
                      >×</button>
                    </div>
                  ))}
                  <div className="text-right text-[11px] text-gray-500 mt-1">
                    Sous-total charges custom : <span className="font-semibold text-gray-700">{fmt(live.customAnnualChargesTotal)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectAnalysisInline;
